import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { curatorGroupsApi } from '@/api/curatorGroups';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { curatorGroupsErrorText } from '@/lib/curatorGroupErrors';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MBlock, MEmpty, MRow, MTag, MTitle } from '@/components/mobile/MobileKit';

/**
 * «Мои марафоны» — курсы подтипов куратора (D40, договор
 * `docs/CONTRACTS/curator-groups.md` §2 и §9).
 *
 * ⚠️ ПУСТОЙ СПИСОК ЗДЕСЬ — ОБЫЧНОЕ ДЕЛО, А НЕ ПОЛОМКА. Подтипы куратору ставит
 * администратор, и на 12.09.2026 они не проставлены ни одному куратору из 90:
 * первым, что увидит каждый, будет именно пустой экран. Поэтому пустое
 * состояние говорит словами, к кому идти, — иначе раздел выглядит сломанным.
 */
export function CuratorGroupsPage() {
  const { t } = useTranslation();

  const courses = useQuery({
    queryKey: ['curator', 'subtype-courses'],
    queryFn: () => curatorGroupsApi.listSubtypeCourses(),
  });

  const items = courses.data ?? [];
  const isMobile = useIsMobile();

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.9) ──────────────────────────
  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle
          title={t('curatorGroups.title')}
          subtitle={courses.isSuccess ? t('curatorGroups.coursesCount', { count: items.length }) : undefined}
        />
        {courses.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : courses.isError ? (
          <p className="px-0.5 text-[13px] text-error">{curatorGroupsErrorText(courses.error, t)}</p>
        ) : !items.length ? (
          <MEmpty icon="groups" title={t('curatorGroups.noSubtypesTitle')} hint={t('curatorGroups.noSubtypesDesc')} />
        ) : (
          <MBlock>
            {items.map((c, i) => (
              <MRow
                key={c.id}
                wrap
                to={`/curator/groups/${c.id}`}
                title={c.title}
                meta={[c.subtype, c.access_category, t('curatorGroups.courseStudents', { count: c.students })]
                  .filter(Boolean)
                  .join(' · ')}
                // Ноль групп — тёплой меткой: с этого начинается работа куратора в потоке без групп.
                aside={
                  <MTag tone={c.groups ? 'info' : 'warm'}>{t('curatorGroups.courseGroups', { count: c.groups })}</MTag>
                }
                last={i === items.length - 1}
              />
            ))}
          </MBlock>
        )}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('curatorGroups.title')}
        subtitle={courses.isLoading ? t('common.loading') : t('curatorGroups.coursesCount', { count: items.length })}
      />

      {courses.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : courses.isError ? (
        <Card variant="subtle" className="text-center text-error">{curatorGroupsErrorText(courses.error, t)}</Card>
      ) : !items.length ? (
        <Card variant="subtle" className="space-y-1 py-8 text-center text-on-surface-variant">
          <p className="font-semibold text-on-surface">{t('curatorGroups.noSubtypesTitle')}</p>
          <p className="text-sm">{t('curatorGroups.noSubtypesDesc')}</p>
        </Card>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {items.map((c) => (
            <li key={c.id}>
              <Link to={`/curator/groups/${c.id}`}>
                <Card interactive className="space-y-2">
                  <p className="font-semibold text-on-surface">{c.title}</p>
                  <div className="flex flex-wrap gap-2">
                    <Pill variant="neutral">{c.subtype}</Pill>
                    {c.access_category && <Pill variant="neutral">{c.access_category}</Pill>}
                    <Pill variant="primary">{t('curatorGroups.courseStudents', { count: c.students })}</Pill>
                    {/* Ноль групп — важнее всего: именно с этого начинается работа
                        куратора в сентябрьском потоке, где групп нет вовсе. */}
                    <Pill variant={c.groups ? 'success' : 'warning'}>
                      {t('curatorGroups.courseGroups', { count: c.groups })}
                    </Pill>
                  </div>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
